import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Minus, PencilSimple, Wrench, DownloadSimple } from "@phosphor-icons/react";
import PerUnitImportDialog from "@/components/PerUnitImportDialog";
import { PER_UNIT_EXPORT_COLUMNS, downloadCsv } from "@/lib/incoming";
import { DeleteStockGuard, EditStockConfirm, fetchStockSummary } from "@/components/StockGuard";

const empty = {
  manufacturer_id: "", series_id: "", name: "", base_sku: "", inventory_type: "count",
  location: "", current_count: 0, low_stock_threshold_count: 2,
  cost_per_unit: "", price_per_unit: "", notes: "",
};

export default function PerUnitProducts() {
  const { isAdmin, canSeePricing, maskMoney } = useAuth();
  const [items, setItems] = useState([]);
  const [mfrs, setMfrs] = useState([]);
  const [series, setSeries] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [adjustOpen, setAdjustOpen] = useState(null);
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [editStock, setEditStock] = useState(null);
  const [confirmEditOpen, setConfirmEditOpen] = useState(false);

  const load = async () => {
    const [{ data: p }, { data: m }, { data: s }] = await Promise.all([
      api.get("/products"), api.get("/manufacturers"), api.get("/series"),
    ]);
    setItems(p.filter((x) => x.inventory_type === "count"));
    setMfrs(m); setSeries(s);
  };
  useEffect(() => { load(); }, []);

  const mfrMap = Object.fromEntries(mfrs.map((m) => [m.id, m]));
  const seriesMap = Object.fromEntries(series.map((s) => [s.id, s]));
  const seriesForForm = series.filter((s) => !form.manufacturer_id || s.manufacturer_id === form.manufacturer_id);

  const openCreate = () => { setEditing(null); setEditStock(null); setForm({ ...empty, manufacturer_id: mfrs[0]?.id || "" }); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setEditStock(null);
    setForm({ ...empty, ...p, cost_per_unit: p.cost_per_unit ?? "", price_per_unit: p.price_per_unit ?? "" });
    setOpen(true);
  };

  const doSave = async () => {
    setConfirmEditOpen(false);
    try {
      const payload = {
        ...form,
        inventory_type: "count",
        current_count: Number(form.current_count || 0),
        low_stock_threshold_count: Number(form.low_stock_threshold_count || 0),
        cost_per_unit: form.cost_per_unit === "" ? null : Number(form.cost_per_unit),
        price_per_unit: form.price_per_unit === "" ? null : Number(form.price_per_unit),
      };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const handleSave = async () => {
    if (!editing) { doSave(); return; }
    // Always re-validate fresh at save time (never trust cached/stale stock state).
    const stock = await fetchStockSummary("product", editing.id);
    if (stock.error) { toast.error("Couldn't verify stock — please try again."); return; }
    setEditStock(stock);
    if (stock.has_stock) { setConfirmEditOpen(true); return; }
    doSave();
  };

  const onDelete = async (p) => {
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const onAdjust = async () => {
    try {
      const delta = Number(adjustDelta);
      if (!delta) return;
      await api.post(`/products/${adjustOpen.id}/adjust-count`, { delta, reason: adjustReason });
      toast.success(`Stock ${delta > 0 ? "increased" : "decreased"} by ${Math.abs(delta)}`);
      setAdjustOpen(null); setAdjustDelta(""); setAdjustReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const onExport = () => {
    if (items.length === 0) { toast.error("No items to export"); return; }
    const data = items.map((p) => [
      p.base_sku,
      mfrMap[p.manufacturer_id]?.name || "",
      seriesMap[p.series_id]?.name || "",
      p.name,
      p.current_count ?? 0,
      p.low_stock_threshold_count ?? "",
      p.location || "",
      p.cost_per_unit ?? "",
      p.price_per_unit ?? "",
    ]);
    downloadCsv(`per-unit-export-${new Date().toISOString().slice(0, 10)}.csv`, PER_UNIT_EXPORT_COLUMNS, data);
    toast.success(`Exported ${items.length} item(s)`);
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="per-unit-products-page">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Inventory</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">Per Unit Products</h1>
          <p className="text-sm text-slate-600 mt-2">Non-vinyl items counted per unit — tools, swatch books, accessories.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onExport} className="gap-2 rounded-md" data-testid="export-per-unit-btn">
            <DownloadSimple size={16} /> Export CSV
          </Button>
          <PerUnitImportDialog onSuccess={load} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} disabled={series.length === 0} className="bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="add-unit-product-btn">
                <Plus size={16} /> Add Item
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Per-Unit Item</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Manufacturer / Brand</Label>
                <Select value={form.manufacturer_id} onValueChange={(v) => setForm({ ...form, manufacturer_id: v, series_id: "" })}>
                  <SelectTrigger data-testid="unit-mfr-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{mfrs.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Series / Category</Label>
                <Select value={form.series_id} onValueChange={(v) => setForm({ ...form, series_id: v })}>
                  <SelectTrigger data-testid="unit-series-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{seriesForForm.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Item name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Squeegee Pro / Swatch Book" data-testid="unit-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">SKU</Label>
                <Input value={form.base_sku} onChange={(e) => setForm({ ...form, base_sku: e.target.value })} placeholder="EMP-SQ-PRO" className="font-mono" data-testid="unit-sku-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Location</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Shelf B2" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Current stock (units)</Label>
                <Input type="number" value={form.current_count} onChange={(e) => setForm({ ...form, current_count: e.target.value })} className="font-mono" data-testid="unit-count-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Low stock threshold</Label>
                <Input type="number" value={form.low_stock_threshold_count} onChange={(e) => setForm({ ...form, low_stock_threshold_count: e.target.value })} className="font-mono" />
              </div>
              {isAdmin && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Cost / unit ($)</Label>
                    <Input type="number" step="0.01" value={form.cost_per_unit} onChange={(e) => setForm({ ...form, cost_per_unit: e.target.value })} className="font-mono" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Price / unit ($)</Label>
                    <Input type="number" step="0.01" value={form.price_per_unit} onChange={(e) => setForm({ ...form, price_per_unit: e.target.value })} className="font-mono" />
                  </div>
                </>
              )}
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
              <Button onClick={handleSave} disabled={!form.name || !form.base_sku || !form.manufacturer_id || !form.series_id}
                className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="unit-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      {items.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-16 text-center">
          <Wrench size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500">No per-unit items yet.</div>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
                <th className="px-4 py-3">Item</th>
                <th className="px-4 py-3">Brand / Category</th>
                <th className="px-4 py-3">SKU</th>
                <th className="px-4 py-3 text-right">Stock</th>
                <th className="px-4 py-3">Location</th>
                {canSeePricing && <th className="px-4 py-3 text-right">Price</th>}
                <th className="px-4 py-3 w-40"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const low = p.current_count <= p.low_stock_threshold_count;
                return (
                  <tr key={p.id} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${low ? "bg-red-50/40" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 grid place-items-center bg-amber-50 border border-amber-200 rounded-sm"><Wrench size={14} className="text-amber-700" /></div>
                        <span className="font-medium">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{mfrMap[p.manufacturer_id]?.name} · {seriesMap[p.series_id]?.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{p.base_sku}</td>
                    <td className={`px-4 py-3 font-mono text-right tabular-nums font-semibold ${low ? "text-red-600" : ""}`}>{p.current_count} units</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{p.location || "—"}</td>
                    {canSeePricing && <td className="px-4 py-3 font-mono text-right tabular-nums text-blue-700">{p.price_per_unit != null ? maskMoney(`$${p.price_per_unit.toFixed(2)}`) : "—"}</td>}
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setAdjustOpen(p)} data-testid={`adjust-unit-${p.id}`} title="Adjust stock">
                          <Plus size={14} /><Minus size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-unit-${p.id}`}><PencilSimple size={14} /></Button>
                        <DeleteStockGuard entityType="product" id={p.id} name={p.name} isStaff={false} onConfirm={() => onDelete(p)} testId={`delete-unit-${p.id}`} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!adjustOpen} onOpenChange={(v) => !v && setAdjustOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adjust stock — {adjustOpen?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="border border-slate-200 bg-slate-50 p-3 rounded-md">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Current</div>
              <div className="font-mono text-2xl font-semibold">{adjustOpen?.current_count ?? 0} units</div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Change (use − to remove, + to add)</Label>
              <Input type="number" value={adjustDelta} onChange={(e) => setAdjustDelta(e.target.value)} placeholder="e.g. -3 or 10" className="font-mono" data-testid="unit-delta-input" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setAdjustDelta(String((Number(adjustDelta) || 0) + 1))} className="flex-1 rounded-md">+1</Button>
              <Button variant="outline" onClick={() => setAdjustDelta(String((Number(adjustDelta) || 0) - 1))} className="flex-1 rounded-md">−1</Button>
              <Button variant="outline" onClick={() => setAdjustDelta(String((Number(adjustDelta) || 0) + 10))} className="flex-1 rounded-md">+10</Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Reason</Label>
              <Input value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} placeholder="Restock, sale, breakage…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustOpen(null)} className="rounded-md">Cancel</Button>
            <Button onClick={onAdjust} disabled={!adjustDelta || Number(adjustDelta) === 0} className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="unit-adjust-confirm">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditStockConfirm open={confirmEditOpen} onOpenChange={setConfirmEditOpen} entityType="product" name={editing?.name} summary={editStock?.summary} onConfirm={doSave} />
    </div>
  );
}
